import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, FileVideo } from 'lucide-react';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { Video } from '@/store/videoDashboard/types';
import { nanoid } from 'nanoid';
import { toast } from '@/hooks/use-toast';

interface VideoUploadInlineProps {
  onUploadComplete?: (videoId: string) => void;
}

export function VideoUploadInline({ onUploadComplete }: VideoUploadInlineProps) {
  useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addVideo, updateVideo } = useVideoDashboardStore();

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast({
        title: translateInterfaceText("Formato inválido"),
        description: translateInterfaceText("Por favor, selecione um arquivo de vídeo"),
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024; // 500MB in bytes
    if (file.size > maxSize) {
      toast({
        title: translateInterfaceText("Arquivo muito grande"),
        description: translateInterfaceText("O vídeo deve ter no máximo 500MB"),
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    // Create video object
    const videoId = nanoid();
    const newVideo: Video = {
      id: videoId,
      title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      description: '',
      thumbnailUrl: 'https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?auto=format&fit=crop&w=400',
      videoUrl: URL.createObjectURL(file),
      uploadDate: new Date().toISOString(),
      duration: 0, // Will be set after processing
      views: 0,
      plays: 0,
      likes: 0,
      folderId: null,
      status: 'processing',
      tags: [],
      createdAt: new Date().toISOString(),
      isNew: true,
      isProcessing: true,
    };

    // Add video to store
    addVideo(newVideo);

    toast({
      title: translateInterfaceText("📤 Upload iniciado"),
      description: file.name,
      duration: 2000,
    });

    // Simulate upload progress
    const uploadInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    // Wait for "upload" to complete
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Simulate processing
    setTimeout(() => {
      const randomDuration = Math.floor(Math.random() * 600) + 300; // 5-15 minutes
      updateVideo(videoId, {
        status: 'processed',
        isProcessing: false,
        duration: randomDuration,
      });

      setIsUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);

      if (onUploadComplete) {
        onUploadComplete(videoId);
      }
    }, 3000);
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
      >
        {isUploading ? (
          <div className="space-y-4">
            <FileVideo className="w-12 h-12 mx-auto text-primary animate-pulse" />
            <div className="space-y-2">
              <p className="font-medium">{translateInterfaceText("Enviando vídeo...")}</p>
              <p className="text-sm text-muted-foreground">{selectedFile?.name}</p>
              <Progress value={uploadProgress} className="w-full" />
              <p className="text-xs text-muted-foreground">{uploadProgress}{translateInterfaceText("% concluído")}</p>
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              {translateInterfaceText("Arraste e solte seu vídeo aqui")}</p>
            <p className="text-xs text-muted-foreground mb-4">{translateInterfaceText("ou")}</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              {translateInterfaceText("Escolher Arquivo")}</Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground mt-4">
              {translateInterfaceText("Formatos suportados: MP4, MOV, AVI • Máximo 500MB")}</p>
          </>
        )}
      </div>
    </div>
  );
}
